import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../../components/ui/collapsible';
import { toast } from 'sonner';
import {
  Save,
  Loader2,
  ArrowLeft,
  Upload,
  Eye,
  Plus,
  X,
  Trash2,
  ChevronDown,
  ChevronUp,
  Users,
  GraduationCap,
  Award,
  Shield,
  Heart,
  Landmark,
  Briefcase,
  Building2,
  Image,
  Download,
  Clock,
  Layers,
  FileText,
  Phone,
  Mail,
  MessageCircle,
  HelpCircle,
  Quote,
  BarChart3,
  ListOrdered,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ICON_OPTIONS = [
  { value: 'users', label: 'Users' },
  { value: 'school', label: 'School' },
  { value: 'award', label: 'Award' },
  { value: 'shield', label: 'Shield' },
  { value: 'heart', label: 'Heart' },
  { value: 'landmark', label: 'Landmark' },
  { value: 'briefcase', label: 'Briefcase' },
  { value: 'building', label: 'Building' },
  { value: 'check', label: 'Check' },
];

const CATEGORY_OPTIONS = ['Schools', 'NGOs', 'Government', 'Corporate', 'Community', 'Other'];

const MAX_IMAGE_SIZE_MB = 2;
const MAX_PDF_SIZE_MB = 10;

const PartnershipsEditor = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const isDirtyRef = useRef(false);
  const [uploading, setUploading] = useState({});
  const [expandedSections, setExpandedSections] = useState({
    hero: true,
    partnerTypes: false,
    logos: false,
    metrics: false,
    process: false,
    caseStudies: false,
    downloads: false,
    contact: false,
    faq: false,
  });

  const [config, setConfig] = useState({
    show_on_homepage: true,
    hero: {
      badge: '',
      title: '',
      subtitle: '',
      cta1_text: '',
      cta1_link: '',
      cta2_text: '',
      cta2_link: null,
    },
    partner_types: [],
    logos: [],
    impact_metrics: [],
    process_steps: [],
    case_studies: [],
    downloads: {
      mou_pdf_url: null,
      schools_pack_pdf_url: null,
    },
    contact: {
      phone: '',
      email: '',
      whatsapp_link: '',
    },
    faqs: [],
    homepage_title: '',
    homepage_subtitle: '',
    updated_at: null,
  });

  const updateConfig = useCallback((updater) => {
    setConfig(prev => typeof updater === 'function' ? updater(prev) : updater);
    setIsDirty(true);
    isDirtyRef.current = true;
  }, []);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isDirtyRef.current) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  const handleNavigateBack = () => {
    if (isDirty && !window.confirm('You have unsaved changes. Leave anyway?')) return;
    navigate(-1);
  };

  useEffect(() => { fetchConfig(); }, []);

  const fetchConfig = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/landing/partnerships-config`);
      if (res.ok) {
        const data = await res.json();
        setConfig(data);
        setIsDirty(false);
        isDirtyRef.current = false;
      }
    } catch (error) {
      toast.error('Failed to load config');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/landing/partnerships-config`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify(config),
      });
      if (res.ok) {
        toast.success('Partnerships saved successfully');
        setIsDirty(false);
        isDirtyRef.current = false;
        fetchConfig();
      } else throw new Error();
    } catch {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // File validation
  const validateImageFile = (file) => {
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Only JPG, PNG, and WebP images are allowed');
      return false;
    }
    if (file.size > MAX_IMAGE_SIZE_MB * 1024 * 1024) {
      toast.warning(`Image is large (${(file.size / 1024 / 1024).toFixed(1)}MB). Recommend under ${MAX_IMAGE_SIZE_MB}MB.`);
    }
    return true;
  };

  const validatePdfFile = (file) => {
    if (file.type !== 'application/pdf') {
      toast.error('Only PDF files are allowed');
      return false;
    }
    if (file.size > MAX_PDF_SIZE_MB * 1024 * 1024) {
      toast.warning(`PDF is large (${(file.size / 1024 / 1024).toFixed(1)}MB). Recommend under ${MAX_PDF_SIZE_MB}MB.`);
    }
    return true;
  };

  const handleFileUpload = async (file, fileType, itemId = null) => {
    const uploadKey = itemId ? `${fileType}_${itemId}` : fileType;
    setUploading(prev => ({ ...prev, [uploadKey]: true }));
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('image_type', fileType);
      if (itemId) formData.append('item_id', itemId);
      const res = await fetch(`${BACKEND_URL}/api/landing/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: formData,
      });
      if (res.ok) {
        const data = await res.json();
        toast.success('File uploaded');
        return data.image_url;
      }
    } catch {
      toast.error('Upload failed');
    } finally {
      setUploading(prev => ({ ...prev, [uploadKey]: false }));
    }
    return null;
  };

  const getFileUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    if (typeof window !== 'undefined') {
      return `${window.location.origin}${url}`;
    }
    return `${BACKEND_URL}${url}`;
  };

  const toggleSection = (key) => {
    setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Hero handlers
  const updateHero = (field, value) => {
    updateConfig(prev => ({
      ...prev,
      hero: { ...prev.hero, [field]: value }
    }));
  };

  // Partner Types handlers
  const addPartnerType = () => {
    if (config.partner_types.length >= 6) return toast.error('Maximum 6 partner types');
    updateConfig(prev => ({
      ...prev,
      partner_types: [...prev.partner_types, {
        id: `pt-${Date.now()}`,
        title: 'New Partner Type',
        description: '',
        icon: 'building'
      }]
    }));
  };

  const updatePartnerType = (id, field, value) => {
    updateConfig(prev => ({
      ...prev,
      partner_types: prev.partner_types.map(pt => pt.id === id ? { ...pt, [field]: value } : pt)
    }));
  };

  const removePartnerType = (id) => {
    updateConfig(prev => ({ ...prev, partner_types: prev.partner_types.filter(pt => pt.id !== id) }));
  };

  // Logo handlers
  const addLogo = () => {
    updateConfig(prev => ({
      ...prev,
      logos: [...prev.logos, {
        id: `logo-${Date.now()}`,
        url: null,
        alt: '',
        category: 'Other',
        order: prev.logos.length + 1
      }]
    }));
  };

  const updateLogo = (id, field, value) => {
    updateConfig(prev => ({
      ...prev,
      logos: prev.logos.map(l => l.id === id ? { ...l, [field]: value } : l)
    }));
  };

  const removeLogo = (id) => {
    updateConfig(prev => ({ ...prev, logos: prev.logos.filter(l => l.id !== id) }));
  };

  const handleLogoUpload = async (file, logoId) => {
    if (!validateImageFile(file)) return;
    const url = await handleFileUpload(file, 'partnerships_logo', logoId);
    if (url) updateLogo(logoId, 'url', url);
  };

  // Metrics handlers
  const updateMetric = (id, field, value) => {
    updateConfig(prev => ({
      ...prev,
      impact_metrics: prev.impact_metrics.map(m => m.id === id ? { ...m, [field]: value } : m)
    }));
  };

  // Process Steps handlers
  const updateProcessStep = (id, field, value) => {
    updateConfig(prev => ({
      ...prev,
      process_steps: prev.process_steps.map(s => s.id === id ? { ...s, [field]: value } : s)
    }));
  };

  // Case Studies handlers
  const addCaseStudy = () => {
    if (config.case_studies.length >= 6) return toast.error('Maximum 6 case studies');
    updateConfig(prev => ({
      ...prev,
      case_studies: [...prev.case_studies, {
        id: `cs-${Date.now()}`,
        title: 'New Case Study',
        partner_name: '',
        challenge: '',
        solution: '',
        results: '',
        quote: '',
        headshot_url: null
      }]
    }));
  };

  const updateCaseStudy = (id, field, value) => {
    updateConfig(prev => ({
      ...prev,
      case_studies: prev.case_studies.map(cs => cs.id === id ? { ...cs, [field]: value } : cs)
    }));
  };

  const removeCaseStudy = (id) => {
    updateConfig(prev => ({ ...prev, case_studies: prev.case_studies.filter(cs => cs.id !== id) }));
  };

  const handleHeadshotUpload = async (file, caseStudyId) => {
    if (!validateImageFile(file)) return;
    const url = await handleFileUpload(file, 'partnerships_headshot', caseStudyId);
    if (url) updateCaseStudy(caseStudyId, 'headshot_url', url);
  };

  // PDF Upload handlers
  const handleMouUpload = async (file) => {
    if (!validatePdfFile(file)) return;
    const url = await handleFileUpload(file, 'partnerships_mou_pdf');
    if (url) {
      updateConfig(prev => ({
        ...prev,
        downloads: { ...prev.downloads, mou_pdf_url: url }
      }));
    }
  };

  const handlePackUpload = async (file) => {
    if (!validatePdfFile(file)) return;
    const url = await handleFileUpload(file, 'partnerships_pack_pdf');
    if (url) {
      updateConfig(prev => ({
        ...prev,
        downloads: { ...prev.downloads, schools_pack_pdf_url: url }
      }));
    }
  };

  // Contact handlers
  const updateContact = (field, value) => {
    updateConfig(prev => ({
      ...prev,
      contact: { ...prev.contact, [field]: value }
    }));
  };

  // FAQ handlers
  const addFaq = () => {
    if (config.faqs.length >= 6) return toast.error('Maximum 6 FAQs');
    updateConfig(prev => ({
      ...prev,
      faqs: [...prev.faqs, { id: `faq-${Date.now()}`, question: '', answer: '' }]
    }));
  };

  const updateFaq = (id, field, value) => {
    updateConfig(prev => ({
      ...prev,
      faqs: prev.faqs.map(f => f.id === id ? { ...f, [field]: value } : f)
    }));
  };

  const removeFaq = (id) => {
    updateConfig(prev => ({ ...prev, faqs: prev.faqs.filter(f => f.id !== id) }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in max-w-5xl mx-auto p-6" data-testid="partnerships-editor">
      {/* Header */}
      <div className="flex items-center justify-between sticky top-0 bg-slate-50 py-4 -mx-6 px-6 z-10 border-b">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={handleNavigateBack}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-heading font-bold text-slate-900">Partnerships</h1>
            <div className="flex items-center gap-3 text-sm text-slate-500">
              <span>Manage partnership page content</span>
              {config.updated_at && (
                <span className="flex items-center gap-1 text-xs text-slate-400">
                  <Clock className="w-3 h-3" />
                  Updated {new Date(config.updated_at).toLocaleDateString()} at {new Date(config.updated_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" onClick={() => window.open('/partner-with-us', '_blank')}>
            <Eye className="w-4 h-4 mr-2" />
            Preview
          </Button>
          <Button onClick={handleSave} disabled={saving} className="bg-primary" data-testid="save-partnerships-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save
          </Button>
        </div>
      </div>

      {/* Hero Section */}
      <Collapsible open={expandedSections.hero} onOpenChange={() => toggleSection('hero')}>
        <Card>
          <CollapsibleTrigger asChild>
            <CardHeader className="cursor-pointer hover:bg-slate-50">
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Layers className="w-5 h-5 text-primary" />
                  Hero & Homepage Settings
                </div>
                {expandedSections.hero ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </CardTitle>
            </CardHeader>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <CardContent className="space-y-6 pt-0">
              {/* Show on Homepage Toggle */}
              <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-lg">
                <Switch
                  checked={config.show_on_homepage}
                  onCheckedChange={v => updateConfig(p => ({ ...p, show_on_homepage: v }))}
                />
                <div>
                  <Label className="font-medium">Show on Homepage</Label>
                  <p className="text-xs text-slate-500">Display partnerships teaser section on the landing page</p>
                </div>
              </div>

              {/* Hero Fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Badge Text</Label>
                  <Input value={config.hero?.badge || ''} onChange={e => updateHero('badge', e.target.value)} placeholder="Partner With Us" />
                </div>
                <div>
                  <Label>Title</Label>
                  <Input value={config.hero?.title || ''} onChange={e => updateHero('title', e.target.value)} placeholder="Transform Lives Through Swimming" />
                </div>
              </div>
              <div>
                <Label>Subtitle</Label>
                <Textarea value={config.hero?.subtitle || ''} onChange={e => updateHero('subtitle', e.target.value)} rows={2} />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Primary CTA Text</Label>
                  <Input value={config.hero?.cta1_text || ''} onChange={e => updateHero('cta1_text', e.target.value)} placeholder="Book a Call" />
                </div>
                <div>
                  <Label>Primary CTA Link</Label>
                  <Input value={config.hero?.cta1_link || ''} onChange={e => updateHero('cta1_link', e.target.value)} placeholder="/#contact" />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Secondary CTA Text</Label>
                  <Input value={config.hero?.cta2_text || ''} onChange={e => updateHero('cta2_text', e.target.value)} placeholder="Download Pack" />
                </div>
              </div>

              <hr />

              {/* Homepage Section */}
              <h4 className="font-medium text-slate-700">Homepage Teaser</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Homepage Title</Label>
                  <Input value={config.homepage_title || ''} onChange={e => updateConfig(p => ({ ...p, homepage_title: e.target.value }))} />
                </div>
                <div>
                  <Label>Homepage Subtitle</Label>
                  <Input value={config.homepage_subtitle || ''} onChange={e => updateConfig(p => ({ ...p, homepage_subtitle: e.target.value }))} />
                </div>
              </div>
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>

      {/* Partner Types */}
      <Collapsible open={expandedSections.partnerTypes} onOpenChange={() => toggleSection('partnerTypes')}>
        <Card>
          <CollapsibleTrigger asChild>
            <CardHeader className="cursor-pointer hover:bg-slate-50">
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-primary" />
                  Partner Types ({config.partner_types?.length || 0}/6)
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); addPartnerType(); }} disabled={config.partner_types?.length >= 6}>
                    <Plus className="w-4 h-4 mr-1" />
                    Add
                  </Button>
                  {expandedSections.partnerTypes ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                </div>
              </CardTitle>
            </CardHeader>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <CardContent className="space-y-4 pt-0">
              {config.partner_types?.map(pt => (
                <div key={pt.id} className="p-4 bg-slate-50 rounded-lg space-y-3 relative">
                  <Button variant="ghost" size="icon" onClick={() => removePartnerType(pt.id)} className="absolute top-2 right-2 text-red-500">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                      <Label className="text-xs">Title</Label>
                      <Input value={pt.title} onChange={e => updatePartnerType(pt.id, 'title', e.target.value)} />
                    </div>
                    <div>
                      <Label className="text-xs">Icon</Label>
                      <Select value={pt.icon} onValueChange={v => updatePartnerType(pt.id, 'icon', v)}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {ICON_OPTIONS.map(opt => (
                            <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">Description</Label>
                    <Textarea value={pt.description} onChange={e => updatePartnerType(pt.id, 'description', e.target.value)} rows={2} />
                  </div>
                </div>
              ))}
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>

      {/* Logos Manager */}
      <Collapsible open={expandedSections.logos} onOpenChange={() => toggleSection('logos')}>
        <Card>
          <CollapsibleTrigger asChild>
            <CardHeader className="cursor-pointer hover:bg-slate-50">
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Image className="w-5 h-5 text-primary" />
                  Partner Logos ({config.logos?.length || 0})
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); addLogo(); }}>
                    <Plus className="w-4 h-4 mr-1" />
                    Add Logo
                  </Button>
                  {expandedSections.logos ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                </div>
              </CardTitle>
            </CardHeader>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <CardContent className="space-y-4 pt-0">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {config.logos?.map(logo => (
                  <div key={logo.id} className="p-4 bg-slate-50 rounded-lg space-y-3 relative">
                    <Button variant="ghost" size="icon" onClick={() => removeLogo(logo.id)} className="absolute top-2 right-2 text-red-500 z-10">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                    
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      className="hidden"
                      id={`logo-${logo.id}`}
                      onChange={e => e.target.files?.[0] && handleLogoUpload(e.target.files[0], logo.id)}
                    />
                    <label
                      htmlFor={`logo-${logo.id}`}
                      className="block aspect-video rounded-lg border-2 border-dashed border-slate-300 hover:border-primary cursor-pointer overflow-hidden bg-white"
                    >
                      {uploading[`partnerships_logo_${logo.id}`] ? (
                        <div className="h-full flex items-center justify-center">
                          <Loader2 className="w-6 h-6 animate-spin" />
                        </div>
                      ) : logo.url ? (
                        <img src={getFileUrl(logo.url)} alt={logo.alt} className="w-full h-full object-contain p-2" />
                      ) : (
                        <div className="h-full flex flex-col items-center justify-center text-slate-400">
                          <Image className="w-8 h-8 mb-1" />
                          <span className="text-xs">Upload Logo</span>
                        </div>
                      )}
                    </label>
                    
                    <Input value={logo.alt} onChange={e => updateLogo(logo.id, 'alt', e.target.value)} placeholder="Alt text" className="text-sm" />
                    
                    <Select value={logo.category} onValueChange={v => updateLogo(logo.id, 'category', v)}>
                      <SelectTrigger className="text-sm"><SelectValue placeholder="Category" /></SelectTrigger>
                      <SelectContent>
                        {CATEGORY_OPTIONS.map(cat => (
                          <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>

      {/* Impact Metrics */}
      <Collapsible open={expandedSections.metrics} onOpenChange={() => toggleSection('metrics')}>
        <Card>
          <CollapsibleTrigger asChild>
            <CardHeader className="cursor-pointer hover:bg-slate-50">
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-primary" />
                  Impact Metrics (4)
                </div>
                {expandedSections.metrics ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </CardTitle>
            </CardHeader>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <CardContent className="space-y-4 pt-0">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {config.impact_metrics?.slice(0, 4).map(metric => (
                  <div key={metric.id} className="p-4 bg-slate-50 rounded-lg space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">Value</Label>
                        <Input value={metric.value} onChange={e => updateMetric(metric.id, 'value', e.target.value)} placeholder="2,500+" />
                      </div>
                      <div>
                        <Label className="text-xs">Label</Label>
                        <Input value={metric.label} onChange={e => updateMetric(metric.id, 'label', e.target.value)} placeholder="Students Trained" />
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs">Icon</Label>
                      <Select value={metric.icon} onValueChange={v => updateMetric(metric.id, 'icon', v)}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {ICON_OPTIONS.map(opt => (
                            <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>

      {/* Process Steps */}
      <Collapsible open={expandedSections.process} onOpenChange={() => toggleSection('process')}>
        <Card>
          <CollapsibleTrigger asChild>
            <CardHeader className="cursor-pointer hover:bg-slate-50">
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <ListOrdered className="w-5 h-5 text-primary" />
                  Partnership Process (4 Steps)
                </div>
                {expandedSections.process ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </CardTitle>
            </CardHeader>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <CardContent className="space-y-4 pt-0">
              {config.process_steps?.slice(0, 4).map((step, idx) => (
                <div key={step.id} className="p-4 bg-slate-50 rounded-lg space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center font-bold">
                      {idx + 1}
                    </div>
                    <Input value={step.title} onChange={e => updateProcessStep(step.id, 'title', e.target.value)} placeholder="Step title" className="flex-1" />
                  </div>
                  <Textarea value={step.description} onChange={e => updateProcessStep(step.id, 'description', e.target.value)} rows={2} placeholder="Description" />
                </div>
              ))}
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>

      {/* Case Studies */}
      <Collapsible open={expandedSections.caseStudies} onOpenChange={() => toggleSection('caseStudies')}>
        <Card>
          <CollapsibleTrigger asChild>
            <CardHeader className="cursor-pointer hover:bg-slate-50">
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Quote className="w-5 h-5 text-primary" />
                  Case Studies ({config.case_studies?.length || 0}/6)
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); addCaseStudy(); }} disabled={config.case_studies?.length >= 6}>
                    <Plus className="w-4 h-4 mr-1" />
                    Add
                  </Button>
                  {expandedSections.caseStudies ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                </div>
              </CardTitle>
            </CardHeader>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <CardContent className="space-y-4 pt-0">
              {config.case_studies?.map(cs => (
                <div key={cs.id} className="p-4 border rounded-lg space-y-4 bg-white relative">
                  <Button variant="ghost" size="icon" onClick={() => removeCaseStudy(cs.id)} className="absolute top-2 right-2 text-red-500">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs">Title</Label>
                      <Input value={cs.title} onChange={e => updateCaseStudy(cs.id, 'title', e.target.value)} />
                    </div>
                    <div>
                      <Label className="text-xs">Partner Name</Label>
                      <Input value={cs.partner_name} onChange={e => updateCaseStudy(cs.id, 'partner_name', e.target.value)} />
                    </div>
                  </div>
                  
                  <div>
                    <Label className="text-xs">Challenge</Label>
                    <Textarea value={cs.challenge} onChange={e => updateCaseStudy(cs.id, 'challenge', e.target.value)} rows={2} />
                  </div>
                  
                  <div>
                    <Label className="text-xs">Solution</Label>
                    <Textarea value={cs.solution} onChange={e => updateCaseStudy(cs.id, 'solution', e.target.value)} rows={2} />
                  </div>
                  
                  <div>
                    <Label className="text-xs">Results</Label>
                    <Textarea value={cs.results} onChange={e => updateCaseStudy(cs.id, 'results', e.target.value)} rows={2} />
                  </div>
                  
                  <div>
                    <Label className="text-xs">Quote (optional)</Label>
                    <Textarea value={cs.quote} onChange={e => updateCaseStudy(cs.id, 'quote', e.target.value)} rows={2} />
                  </div>
                </div>
              ))}
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>

      {/* Downloads */}
      <Collapsible open={expandedSections.downloads} onOpenChange={() => toggleSection('downloads')}>
        <Card>
          <CollapsibleTrigger asChild>
            <CardHeader className="cursor-pointer hover:bg-slate-50">
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-primary" />
                  Downloadable PDFs
                </div>
                {expandedSections.downloads ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </CardTitle>
            </CardHeader>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <CardContent className="space-y-4 pt-0">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* MoU PDF */}
                <div className="p-4 bg-slate-50 rounded-lg">
                  <Label className="text-sm font-medium mb-2 block">MoU Template</Label>
                  <input
                    type="file"
                    accept="application/pdf"
                    className="hidden"
                    id="mou-pdf"
                    onChange={e => e.target.files?.[0] && handleMouUpload(e.target.files[0])}
                  />
                  <label htmlFor="mou-pdf" className="flex items-center gap-2 px-4 py-3 border-2 border-dashed rounded-lg cursor-pointer hover:border-primary">
                    {uploading['partnerships_mou_pdf'] ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        <Upload className="w-5 h-5" />
                        {config.downloads?.mou_pdf_url ? 'Replace PDF' : 'Upload MoU PDF'}
                      </>
                    )}
                  </label>
                  {config.downloads?.mou_pdf_url && (
                    <a href={getFileUrl(config.downloads.mou_pdf_url)} target="_blank" rel="noopener noreferrer" className="text-sm text-primary hover:underline mt-2 inline-flex items-center gap-1">
                      <Eye className="w-4 h-4" /> View current PDF
                    </a>
                  )}
                </div>

                {/* Schools Pack PDF */}
                <div className="p-4 bg-slate-50 rounded-lg">
                  <Label className="text-sm font-medium mb-2 block">Schools Partnership Pack</Label>
                  <input
                    type="file"
                    accept="application/pdf"
                    className="hidden"
                    id="pack-pdf"
                    onChange={e => e.target.files?.[0] && handlePackUpload(e.target.files[0])}
                  />
                  <label htmlFor="pack-pdf" className="flex items-center gap-2 px-4 py-3 border-2 border-dashed rounded-lg cursor-pointer hover:border-primary">
                    {uploading['partnerships_pack_pdf'] ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        <Upload className="w-5 h-5" />
                        {config.downloads?.schools_pack_pdf_url ? 'Replace PDF' : 'Upload Pack PDF'}
                      </>
                    )}
                  </label>
                  {config.downloads?.schools_pack_pdf_url && (
                    <a href={getFileUrl(config.downloads.schools_pack_pdf_url)} target="_blank" rel="noopener noreferrer" className="text-sm text-primary hover:underline mt-2 inline-flex items-center gap-1">
                      <Eye className="w-4 h-4" /> View current PDF
                    </a>
                  )}
                </div>
              </div>
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>

      {/* Contact & FAQ */}
      <Collapsible open={expandedSections.contact} onOpenChange={() => toggleSection('contact')}>
        <Card>
          <CollapsibleTrigger asChild>
            <CardHeader className="cursor-pointer hover:bg-slate-50">
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Phone className="w-5 h-5 text-primary" />
                  Contact Info
                </div>
                {expandedSections.contact ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </CardTitle>
            </CardHeader>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <CardContent className="space-y-4 pt-0">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label>Phone</Label>
                  <Input value={config.contact?.phone || ''} onChange={e => updateContact('phone', e.target.value)} />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input value={config.contact?.email || ''} onChange={e => updateContact('email', e.target.value)} />
                </div>
                <div>
                  <Label>WhatsApp Link</Label>
                  <Input value={config.contact?.whatsapp_link || ''} onChange={e => updateContact('whatsapp_link', e.target.value)} placeholder="https://wa.me/..." />
                </div>
              </div>
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>

      {/* FAQ */}
      <Collapsible open={expandedSections.faq} onOpenChange={() => toggleSection('faq')}>
        <Card>
          <CollapsibleTrigger asChild>
            <CardHeader className="cursor-pointer hover:bg-slate-50">
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <HelpCircle className="w-5 h-5 text-primary" />
                  FAQ ({config.faqs?.length || 0}/6)
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); addFaq(); }} disabled={config.faqs?.length >= 6}>
                    <Plus className="w-4 h-4 mr-1" />
                    Add
                  </Button>
                  {expandedSections.faq ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                </div>
              </CardTitle>
            </CardHeader>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <CardContent className="space-y-4 pt-0">
              {config.faqs?.map(faq => (
                <div key={faq.id} className="p-4 bg-slate-50 rounded-lg space-y-3 relative">
                  <Button variant="ghost" size="icon" onClick={() => removeFaq(faq.id)} className="absolute top-2 right-2 text-red-500">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                  <div>
                    <Label className="text-xs">Question</Label>
                    <Input value={faq.question} onChange={e => updateFaq(faq.id, 'question', e.target.value)} />
                  </div>
                  <div>
                    <Label className="text-xs">Answer</Label>
                    <Textarea value={faq.answer} onChange={e => updateFaq(faq.id, 'answer', e.target.value)} rows={2} />
                  </div>
                </div>
              ))}
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>

      {/* Bottom Save */}
      <div className="flex justify-end pb-8">
        <Button onClick={handleSave} disabled={saving} size="lg" className="bg-primary">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Save All Changes
        </Button>
      </div>
    </div>
  );
};

export default PartnershipsEditor;
